import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import axios from "../../axios";
import useWebRTC from "../../hooks/useWebRTC";
import "./VideoRoom.css";

const END_REASON_MESSAGES = {
  TIMER_EXPIRED: "Session time has ended",
  USER_LEFT: "The other participant has ended the session",
  NETWORK_FAILURE: "Connection was lost",
  ALREADY_EXPIRED: "This session has already ended",
  RECOVERED_EXPIRED: "This session has already ended",
  SWEEP_EXPIRED: "This session has already ended",
  USER_INITIATED: "You ended the session",
};

const VideoRoom = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const [meetingToken, setMeetingToken] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [warningCountdown, setWarningCountdown] = useState(60);
  const [showThankYou, setShowThankYou] = useState(false);
  const [endReason, setEndReason] = useState(null);
  const [otherParticipantLeft, setOtherParticipantLeft] = useState(false);
  const [connectionStatusText, setConnectionStatusText] =
    useState("Connecting...");
  const [meetingEndTime, setMeetingEndTime] = useState(null);
  const warningTimerRef = useRef(null);

  // Fetch meeting token and status on mount
  useEffect(() => {
    const fetchMeetingData = async () => {
      try {
        setLoading(true);
        const [tokenRes, statusRes] = await Promise.all([
          axios.get(`/meetings/${appointmentId}/token`),
          axios.get(`/meetings/${appointmentId}/status`),
        ]);
        const { token, role: userRole } = tokenRes.data.data;
        setMeetingToken(token);
        setRole(userRole);

        const { date, endTime } = statusRes.data.data;
        if (date && endTime) {
          setMeetingEndTime(dayjs(`${date} ${endTime}`, "YYYY-MM-DD HH:mm"));
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to join the meeting");
      } finally {
        setLoading(false);
      }
    };
    fetchMeetingData();
  }, [appointmentId]);

  // Handle meeting warning (T-60s)
  const handleWarning = useCallback((payload) => {
    setShowWarning(true);
    setWarningCountdown(payload.remainingSeconds || 60);
  }, []);

  const redirectToBookingsWithRating = useCallback(() => {
    if (role === "CUSTOMER" && appointmentId) {
      navigate(`/bookings?rateAppointmentId=${appointmentId}`, {
        state: { showRatingAppointmentId: appointmentId },
      });
      return;
    }

    navigate("/bookings");
  }, [navigate, appointmentId, role]);

  // Handle meeting ended
  const handleMeetingEnded = useCallback(
    (payload) => {
      const reason = payload?.reason || "USER_LEFT";
      setEndReason(reason);
      setShowWarning(false);

      // Stop warning timer
      if (warningTimerRef.current) {
        clearInterval(warningTimerRef.current);
        warningTimerRef.current = null;
      }

      // Show Thank You screen
      setShowThankYou(true);

      // Auto redirect after 5 seconds
      setTimeout(() => {
        redirectToBookingsWithRating();
      }, 5000);
    },
    [redirectToBookingsWithRating],
  );

  // Handle participant joined
  const handleParticipantJoined = useCallback(() => {
    setConnectionStatusText("Connected");
    setOtherParticipantLeft(false);
  }, []);

  // Handle participant left
  const handleParticipantLeft = useCallback(() => {
    setOtherParticipantLeft(true);
    setConnectionStatusText("Other participant left");
  }, []);

  const handleJoinError = useCallback((message) => {
    setError(message || "Failed to join the meeting");
  }, []);

  // WebRTC hook
  const {
    localStream,
    remoteStream,
    connectionStatus,
    isMicMuted,
    isCameraOff,
    toggleMic,
    toggleCamera,
    endCall,
  } = useWebRTC({
    appointmentId,
    token: meetingToken,
    role,
    onMeetingEnded: handleMeetingEnded,
    onWarning: handleWarning,
    onParticipantJoined: handleParticipantJoined,
    onParticipantLeft: handleParticipantLeft,
    onJoinError: handleJoinError,
  });

  // Update connection status text
  useEffect(() => {
    switch (connectionStatus) {
      case "DISCONNECTED":
        setConnectionStatusText("Disconnected");
        break;
      case "CONNECTING":
        setConnectionStatusText("Connecting...");
        break;
      case "WAITING":
        setConnectionStatusText("Waiting for other participant...");
        break;
      case "NEGOTIATING":
        setConnectionStatusText("Establishing connection...");
        break;
      case "CONNECTED":
        setConnectionStatusText("Connected");
        break;
      default:
        break;
    }
  }, [connectionStatus]);

  // Warning countdown timer
  useEffect(() => {
    if (showWarning && warningCountdown > 0) {
      warningTimerRef.current = setInterval(() => {
        setWarningCountdown((prev) => {
          if (prev <= 1) {
            if (warningTimerRef.current) clearInterval(warningTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (warningTimerRef.current) clearInterval(warningTimerRef.current);
    };
  }, [showWarning, warningCountdown]);

  // Local countdown fallback — ensures the meeting ends even if the
  // WebSocket signaling connection is unavailable.
  useEffect(() => {
    if (!meetingEndTime || showThankYou) return;

    let warningShown = false;

    const tick = () => {
      const remainingMs = meetingEndTime.diff(dayjs());
      const remainingSeconds = Math.max(Math.ceil(remainingMs / 1000), 0);

      if (!warningShown && remainingSeconds <= 60) {
        warningShown = true;
        setShowWarning(true);
        setWarningCountdown(remainingSeconds);
      }

      if (remainingSeconds <= 0) {
        handleMeetingEnded({ reason: "TIMER_EXPIRED" });
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [meetingEndTime, showThankYou, handleMeetingEnded]);

  // Handle end call button
  const handleEndCall = () => {
    endCall();
    setEndReason("USER_INITIATED");
    setShowWarning(false);
    if (warningTimerRef.current) {
      clearInterval(warningTimerRef.current);
    }
    setShowThankYou(true);
    setTimeout(() => {
      redirectToBookingsWithRating();
    }, 5000);
  };

  // Loading state
  if (loading) {
    return (
      <div className="video-room-loading">
        <div className="loading-spinner"></div>
        <p>Setting up your session...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="video-room-error">
        <span className="material-symbols-outlined error-icon">error</span>
        <h2>Unable to Join Session</h2>
        <p>{error}</p>
        <button className="back-btn" onClick={() => navigate("/bookings")}>
          Back to Bookings
        </button>
      </div>
    );
  }

  // Thank You screen
  if (showThankYou) {
    return (
      <div className="thank-you-overlay">
        <div className="thank-you-content">
          <span className="material-symbols-outlined thank-you-icon">
            check_circle
          </span>
          <h1>Thank You!</h1>
          <p className="thank-you-message">
            {END_REASON_MESSAGES[endReason] || "Session ended"}
          </p>
          <p className="thank-you-redirect">Redirecting to bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="video-room-container">
      {/* Remote video (full screen) */}
      <div className="remote-video-container">
        {remoteStream ? (
          <video
            className="remote-video"
            autoPlay
            playsInline
            ref={(video) => {
              if (video) video.srcObject = remoteStream;
            }}
          />
        ) : (
          <div className="remote-video-placeholder">
            <span className="material-symbols-outlined">person</span>
            <p>{connectionStatusText}</p>
          </div>
        )}
      </div>

      {/* Local video (PiP) */}
      <div className="local-video-container">
        <video
          className={`local-video ${isCameraOff ? "camera-off" : ""}`}
          autoPlay
          playsInline
          muted
          ref={(video) => {
            if (video && localStream) video.srcObject = localStream;
          }}
        />
        {isCameraOff && (
          <div className="camera-off-indicator">
            <span className="material-symbols-outlined">videocam_off</span>
          </div>
        )}
        <div className="local-video-label">
          {isMicMuted ? (
            <span className="material-symbols-outlined mic-icon muted">
              mic_off
            </span>
          ) : (
            <span className="material-symbols-outlined mic-icon">mic</span>
          )}
          You
        </div>
      </div>

      {/* Other participant left indicator */}
      {otherParticipantLeft && (
        <div className="participant-left-banner">
          <span className="material-symbols-outlined">person_off</span>
          The other participant has left
        </div>
      )}

      {/* Warning sidebar (T-60s) */}
      {showWarning && (
        <div className="warning-sidebar">
          <div className="warning-sidebar-content">
            <span className="material-symbols-outlined warning-icon">
              timer
            </span>
            <h3>Session Ending Soon</h3>
            <p className="warning-countdown">
              {Math.floor(warningCountdown / 60)}:
              {(warningCountdown % 60).toString().padStart(2, "0")}
            </p>
            <p className="warning-text">remaining</p>
            <div className="warning-progress-bar">
              <div
                className="warning-progress-fill"
                style={{
                  width: `${(warningCountdown / 60) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Controls bar */}
      <div className="controls-bar">
        <button
          className={`control-btn ${isMicMuted ? "control-btn-active" : ""}`}
          onClick={toggleMic}
          title={isMicMuted ? "Unmute microphone" : "Mute microphone"}
        >
          <span className="material-symbols-outlined">
            {isMicMuted ? "mic_off" : "mic"}
          </span>
        </button>

        <button
          className={`control-btn ${isCameraOff ? "control-btn-active" : ""}`}
          onClick={toggleCamera}
          title={isCameraOff ? "Turn on camera" : "Turn off camera"}
        >
          <span className="material-symbols-outlined">
            {isCameraOff ? "videocam_off" : "videocam"}
          </span>
        </button>

        <button
          className="control-btn end-call-btn"
          onClick={handleEndCall}
          title="End session"
        >
          <span className="material-symbols-outlined">call_end</span>
        </button>
      </div>
    </div>
  );
};

export default VideoRoom;
